import React from 'react';
import { Box, Button, Typography, Fade, Chip, CircularProgress, Grid } from '@mui/material';
import {
  SmartToy as SmartToyIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  FilePresent as FilePresentIcon,
  DeleteSweepOutlined as DeleteSweepIcon,
  Close as CloseIcon,
  StopOutlined as StopIcon,
} from '@mui/icons-material';
import { BotMessage } from '../hooks/useChatbotState';
import { DEFAULT_QUICK_ACTIONS } from '../constants/quickActions';
import { formatSuggestionLabel } from '../utils/formatSuggestionLabel';
import { ChatActionGroup } from './ChatActionGroup';
import { ChatSuggestionChip } from './ChatSuggestionChip';
import { QuickActionCard } from './QuickActionCard';

interface ChatMessagesProps {
  messages: BotMessage[];
  isLoading: boolean;
  isActivating: boolean;
  handleQuickAction: (action: any) => void;
  handleBotStateAction: (action: 'yes' | 'no' | 'open-form', url?: string) => void;
  handleRelatedChipClick: (chip: any) => void;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onEndConversation: () => void;
  dismissConfirmation: (messageId: string) => void;
  handleEndConversation: () => Promise<void>;
  handleClearChat: () => void;
  handleMissingFieldClick: (field: string) => void;
}

export const ChatMessages: React.FC<ChatMessagesProps> = React.memo(
  ({
    messages,
    isLoading,
    isActivating,
    handleQuickAction,
    handleBotStateAction,
    handleRelatedChipClick,
    messagesEndRef,
    onEndConversation,
    dismissConfirmation,
    handleEndConversation,
    handleClearChat,
    handleMissingFieldClick,
  }) => {
    const renderBotStateUI = (message: BotMessage) => {
      const state = message.state?.toUpperCase();
      switch (state) {
        case 'CONFIRM_CLEAR_CHAT':
          return (
            <ChatActionGroup
              actions={[
                {
                  label: 'Clear Chat',
                  icon: DeleteSweepIcon,
                  onClick: handleClearChat,
                  variant: 'contained',
                  color: 'primary',
                },
                {
                  label: 'Cancel',
                  icon: CloseIcon,
                  onClick: () => dismissConfirmation(message.id),
                },
              ]}
            />
          );

        case 'CONFIRM_END_CONVERSATION':
          return (
            <ChatActionGroup
              actions={[
                {
                  label: 'End Conversation',
                  icon: StopIcon,
                  onClick: handleEndConversation,
                  variant: 'contained',
                  color: 'error',
                },
                {
                  label: 'Continue Chat',
                  icon: CloseIcon,
                  onClick: () => dismissConfirmation(message.id),
                },
              ]}
            />
          );

        case 'COMPLETE':
        case 'COMPLETED':
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 18, color: 'green' }} />
              <Typography variant="caption" sx={{ fontWeight: 500, color: 'green' }}>
                Conversation complete
              </Typography>
            </Box>
          );

        case 'AWAITING_INPUT':
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
              <Typography variant="caption" sx={{ color: '#E65100', fontWeight: 600 }}>
                ↳ Waiting for required information
              </Typography>
              {message.missingFields &&
                message.missingFields.length > 0 &&
                renderMissingFieldsChips(message.missingFields)}
            </Box>
          );

        case 'PROCESSING':
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={12} sx={{ color: '#111111' }} />
              <Typography variant="caption" sx={{ color: '#757575' }}>
                Processing...
              </Typography>
            </Box>
          );

        case 'CANCELLED':
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography variant="caption" sx={{ color: '#D32F2F', fontWeight: 500 }}>
                Conversation cancelled
              </Typography>
            </Box>
          );

        case 'EXPIRED':
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography variant="caption" sx={{ color: '#757575', fontWeight: 500 }}>
                Conversation session expired
              </Typography>
            </Box>
          );

        case 'AWAITING_CONFIRMATION':
          return (
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => handleBotStateAction('yes')}
                disabled={isLoading}
                sx={{
                  backgroundColor: '#111111',
                  color: 'white',
                  textTransform: 'none',
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: '#222222' },
                  '&:focus-visible': {
                    outline: '2px solid #111111',
                    outlineOffset: '2px',
                  },
                }}
              >
                Yes
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleBotStateAction('no')}
                disabled={isLoading}
                sx={{
                  borderColor: '#EAEAEA',
                  color: '#1A1A1A',
                  textTransform: 'none',
                  borderRadius: '8px',
                  '&:hover': { borderColor: '#B0B0B0', backgroundColor: '#FAFAFA' },
                  '&:focus-visible': {
                    outline: '2px solid #111111',
                    outlineOffset: '2px',
                  },
                }}
              >
                No
              </Button>
            </Box>
          );

        case 'PREFILL_UI':
          return message.uiAction ? (
            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="contained"
                size="small"
                endIcon={<OpenInNewIcon />}
                onClick={() => handleBotStateAction('open-form', message.uiAction!.editUrl)}
                sx={{
                  backgroundColor: '#111111',
                  color: 'white',
                  textTransform: 'none',
                  borderRadius: '8px',
                  mb: 0.5,
                  '&:hover': { backgroundColor: '#222222' },
                  '&:focus-visible': {
                    outline: '2px solid #111111',
                    outlineOffset: '2px',
                  },
                }}
              >
                Open Form
              </Button>
              <Typography variant="caption" sx={{ display: 'block', color: 'gray', fontSize: '10px' }}>
                Form will be pre-filled with suggested data
              </Typography>
            </Box>
          ) : null;

        default:
          return null;
      }
    };

    const renderMissingFieldsChips = (fields?: string[]) => {
      if (!fields || fields.length === 0) return null;
      return (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          {fields.map(field => (
            <ChatSuggestionChip
              key={field}
              label={formatSuggestionLabel(field)}
              onClick={() => handleMissingFieldClick(field)}
              sx={{
                borderColor: '#E65100',
                backgroundColor: '#FFF8E1',
                '&:hover': {
                  backgroundColor: '#FFE082',
                  borderColor: '#F57C00',
                },
              }}
            />
          ))}
        </Box>
      );
    };

    const renderRelatedChips = (chips?: BotMessage['relatedChips']) => {
      if (!chips || chips.length === 0) return null;

      return (
        <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ width: '100%', color: '#757575', mb: 0.5 }}>
            Related topics:
          </Typography>
          {chips.map(chip => (
            <ChatSuggestionChip
              key={chip.id}
              label={chip.label}
              onClick={() => handleRelatedChipClick(chip)}
              sx={{
                fontSize: '11px',
                height: '30px',
              }}
            />
          ))}
        </Box>
      );
    };

    return (
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          backgroundColor: '#FFFFFF',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '10px',
          },
        }}
      >
        {/* Greetings & Static Quick Actions/Suggestions (rendered only when no messages in current screen) */}
        {messages.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 2.5,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '16px',
                backgroundColor: '#111111',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                animation: isActivating ? 'geminiPulse 1.8s ease-in-out infinite' : 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            >
              <SmartToyIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  fontSize: '18px',
                  lineHeight: 1.3,
                  color: '#1A1A1A',
                  mb: 0.5,
                  px: 1,
                }}
              >
                Hello 👋, How can I help you with your shipment today?
              </Typography>
              <Typography variant="body2" sx={{ color: '#757575', fontSize: '13px' }}>
                Pick a quick action or just ask me anything.
              </Typography>
            </Box>

            {/* Quick Actions Section */}
            <Box sx={{ width: '100%', textAlign: 'left', mt: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: '#757575',
                  fontSize: '12px',
                  mb: 1.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Quick Actions
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {DEFAULT_QUICK_ACTIONS.map(action => (
                  <Grid item xs={12} sm={6} key={action.label}>
                    <QuickActionCard
                      label={action.label}
                      icon={action.icon ? React.createElement(action.icon) : <SmartToyIcon sx={{ fontSize: 20 }} />}
                      onClick={() => handleQuickAction(action)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        )}

        {/* Conversation Messages */}
        {messages.map(message => (
          <Fade key={message.id} in>
            {message.role === 'user' ? (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', maxWidth: '75%' }}>
                  <Box
                    sx={{
                      backgroundColor: '#111111',
                      color: '#FFFFFF',
                      padding: '12px 16px',
                      borderRadius: '20px 20px 4px 20px',
                      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '13.5px', lineHeight: 1.5 }}>
                      {message.content}
                    </Typography>

                    {message.attachmentPreviews && message.attachmentPreviews.length > 0 && (
                      <Box sx={{ marginTop: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {message.attachmentPreviews.map((file, idx) => (
                          <Chip
                            key={idx}
                            icon={<FilePresentIcon sx={{ color: '#FFFFFF !important' }} />}
                            label={`${file.name.substring(0, 15)}...`}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              height: 'auto',
                              padding: '4px 8px',
                              color: '#FFFFFF',
                              borderColor: 'rgba(255, 255, 255, 0.3)',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', marginTop: 0.5, marginRight: 0.5, color: '#9E9E9E', fontSize: '11px' }}
                  >
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    backgroundColor: '#111111',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    flexShrink: 0,
                  }}
                >
                  <SmartToyIcon sx={{ fontSize: 16 }} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '75%' }}>
                  <Box
                    sx={{
                      backgroundColor: '#F5F5F7',
                      color: '#1A1A1A',
                      padding: '12px 16px',
                      borderRadius: '20px 20px 20px 4px',
                      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.02)',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '13.5px', lineHeight: 1.5 }}>
                      {message.content}
                    </Typography>

                    {renderBotStateUI(message)}
                    {renderRelatedChips(message.relatedChips)}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', marginTop: 0.5, marginLeft: 0.5, color: '#9E9E9E', fontSize: '11px' }}
                  >
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </Box>
            )}
          </Fade>
        ))}

        {/* Loading Indicator */}
        {isLoading && (
          <Fade in>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  backgroundColor: '#111111',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0,
                }}
              >
                <SmartToyIcon sx={{ fontSize: 16 }} />
              </Box>
              <Box
                sx={{
                  backgroundColor: '#F5F5F7',
                  padding: '12px 16px',
                  borderRadius: '20px 20px 20px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={16} sx={{ color: '#111111' }} />
                <Typography variant="caption" sx={{ color: '#757575', fontSize: '12px' }}>
                  Thinking...
                </Typography>
              </Box>
            </Box>
          </Fade>
        )}

        {/* End Conversation Button inside Chat Body */}
        {messages.length > 0 &&
          !messages.some(m => m.state === 'CONFIRM_CLEAR_CHAT' || m.state === 'CONFIRM_END_CONVERSATION') && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={onEndConversation}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  padding: '6px 16px',
                  borderColor: '#FFCDD2',
                  backgroundColor: '#FFF8F8',
                  '&:hover': {
                    borderColor: '#D32F2F',
                    backgroundColor: '#FFEBEE',
                  },
                  '&:focus-visible': {
                    outline: '2px solid #D32F2F',
                    outlineOffset: '2px',
                  },
                }}
              >
                End Conversation
              </Button>
            </Box>
          )}

        <div ref={messagesEndRef} />
      </Box>
    );
  },
);

ChatMessages.displayName = 'ChatMessages';
